import { all, select, put, call, delay } from 'redux-saga/effects';
import { chunk } from 'lodash';
import i18n from 'i18next';
import { bufferedHandler, callback } from '../../tools/effects';
import { getAcquisitionParameters } from '../../../selectors';
import { ee } from '../../../services/earth-engine';
import * as Metadata from '../../../common/metadata';
import { Timer } from '../../../common/utils';
import { addShape } from '../../../common/map';
import * as Coastline from '../../../algorithms/shoreline';
import { acquireDayMosaic } from '../../../algorithms/satellite/shoreSAvailability';
import { Actions as Map } from '../map';
import { Actions as Snacks } from '../snacks';
import { Actions as Common } from '../common';
import {
  reprojectFeatureCollection,
  reprojectLineString,
  intersectTransectsToCoastlines,
  intersectTransectsWithBaseline,
  keepOnlyIntersectionClosestToBaseline,
  computeDistancesFromBaseline,
  mergeByTimeProximity,
  computeErrorMetrics,
  computeAggregatedErrorMetrics,
  computeMeasuredDeviation,
  computeBrierSkillScore,
  extractReferenceCandidate,
  cleanReferenceCandidate,
  keepUsableCandidates,
  toDisplayFeatures,
  resetCoRegistration,
} from '../../../algorithms/shoreS';
import { Types, Actions } from './header';

const WGS84 = 'EPSG:4326';
const REFERENCE_COASTLINE_GROUP = 'shoreSCoastlineData';

const RAW_COASTLINE_GROUP = 'shoreSRawCoastlineData';
const REFERENCE_COLOR = '#d32f2f';
const RAW_COLOR = '#ef9a9a';
const TRANSECT_COLOR = 'purple';
const BIAS_POSITIVE_COLOR = '#1976d2';
const BIAS_NEGATIVE_COLOR = '#d32f2f';

const EXTRACTION_CHUNK_SIZE = 10;
const CLEANUP_CHUNK_SIZE = 10;

/**
 * @function baselineCoordsToFeature
 * @description Wraps the baseline's raw coordinate list in a GeoJSON LineString Feature, the
 * shape the reprojection and intersection helpers expect.
 * @param {Array<Array<number>>} coords - [[lng, lat], ...] positions.
 * **Called by:
 * @see {handleRun}
 * @memberof module:ShoreS
 * @returns {Object} A GeoJSON LineString Feature.
 */
const baselineCoordsToFeature = (coords) => ({
  type: 'Feature',
  properties: {},
  geometry: { type: 'LineString', coordinates: coords },
});

/**
 * @function evaluateFeatureCollection
 * @description Promisifies Earth Engine's callback-style `evaluate`, so a server-side collection
 * can be awaited with `call` like any other async effect.
 * @param {Object} fc - An ee.FeatureCollection.
 * **Called by:
 * @see {handleRun}
 * @memberof module:ShoreS
 * @returns {Promise<Object>} The evaluated GeoJSON.
 */
const evaluateFeatureCollection = (fc) =>
  new Promise((resolve, reject) => fc.evaluate((data, err) => (err ? reject(err) : resolve(data))));

/**
 * @function normalizeReferenceTimeProperty
 * @description Copies each reference coastline's date from "system:time_start" to `time`.
 * GEE-derived coastlines carry it under the former (see `Metadata.TIME_START`, set in
 * `src/algorithms/shoreline/extraction.js`'s `deriveShoreline`), while
 * `intersectTransectsToCoastlines` reads the latter as its default `coastlineIdProp`.
 * @param {Object} referenceFC - The reference GeoJSON FeatureCollection.
 * **Called by:
 * @see {handleRun}
 * @memberof module:ShoreS
 * @returns {Object} The same collection, with `time` set on every feature.
 */
const normalizeReferenceTimeProperty = (referenceFC) => ({
  ...referenceFC,
  features: referenceFC.features.map((f) => ({
    ...f,
    properties: { ...f.properties, time: f.properties[Metadata.TIME_START] },
  })),
});

/**
 * @function withDateTime
 * @description Revives each record's `time` string into a Date, which the time-proximity merge
 * and the metrics need to compare.
 * @param {Array<Object>} records - Distance records carrying an ISO `time`.
 * **Called by:
 * @see {handleRun}
 * @memberof module:ShoreS
 * @returns {Array<Object>} The same records, with `time` as a Date.
 */
const withDateTime = (records) => records.map((r) => ({ ...r, time: new Date(r.time) }));

/**
 * @function drawFeatures
 * @description Draws a GeoJSON FeatureCollection straight onto the map, bypassing
 * `Map.addEEFeature`. `handleAddEEFeature` exists to `evaluate` a server-side collection first;
 * by the time this saga draws anything it already holds plain GeoJSON, so that round trip
 * buys nothing.
 * @param {Array<Object>} features - LineString features, in WGS84.
 * @param {string} name - i18n key for the legend entry.
 * @param {string|Function} color - A color, or a (feature) => color mapper.
 * @param {string} group - Shape group name.
 * **Called by:
 * @see {handleExtractReferences}
 * @see {handleRun}
 * @see {handleRedrawRaw}
 * @memberof module:ShoreS
 * @returns {void}
 */
const drawFeatures = function* (features, name, color, group) {
  if (!features.length) return;
  const colorOf = typeof color === 'function' ? color : () => color;
  const shapes = features.map((feature) => addShape(feature, colorOf(feature), 1, group)).filter(Boolean);
  if (!shapes.length) return;
  yield put(Map.addShapes(shapes, name, group, features));
};

/**
 * @function extractCandidatesInChunks
 * @description Reads every selected image and extracts its raw candidate coastline, in chunks,
 * reporting progress through the same task snack the shared `deriveInChunks` uses. A failed chunk
 * is retried once before being skipped, so one bad image doesn't sink the whole extraction.
 * @param {Array} selectedImages - Dates chosen in ShoreSPeriodImageChooser.
 * @param {Object} satellite - The selected mission, for its bands and optical resolution.
 * @param {Object} roi - ee.Geometry the extraction is clipped to.
 * @see {@link extractReferenceCandidate} - Runs per image.
 * **Called by:
 * @see {handleExtractReferences}
 * @memberof module:ShoreS
 * @returns {Array<Object>} Evaluated GeoJSON features, nulls already dropped.
 */
const extractCandidatesInChunks = function* (selectedImages, satellite, roi) {
  const total = selectedImages.length;
  const timer = new Timer();
  const evaluated = [];

  /** The title names the job once; the detail is the part that moves. */
  const progress = (done, etc = '') =>
    `${done}/${total}${etc ? ` [${i18n.t('forms.shorelineAnalysis.process.estimate')} ${etc}]` : ''}`;
  const title = i18n.t('forms.shorelineAnalysis.process.extraction');

  yield put(Common.notice(title, progress(0)));

  const chunks = chunk(selectedImages, EXTRACTION_CHUNK_SIZE);

  for (let index = 0; index < chunks.length; index++) {
    const current = chunks[index];

    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        yield delay(10);

        const candidates = [];
        for (const image of current) {
          const mission = satellite.missions.find((m) => m.name === image.name);
          if (!mission) continue;

          yield delay(0);

          try {
            const candidate = extractReferenceCandidate(
              acquireDayMosaic(mission, image.date, roi),
              roi,
              mission.bands,
              mission.opticalResolution,
            );
            if (candidate) candidates.push(candidate);
          } catch (e) {
            console.error('[ShoreS] candidate extraction failed for', image, e);
          }
        }

        if (candidates.length) {
          const job = ee.List(candidates);
          const data = yield callback([job, job.evaluate]);
          evaluated.push(...(data || []).filter(Boolean));
        }

        yield put(Common.notice(title, progress(evaluated.length, timer.format(timer.project(index + 1, chunks.length)))));
        break;
      } catch (e) {
        console.error(`[ShoreS] extraction chunk ${index + 1}/${chunks.length}, attempt ${attempt + 1} failed:`, e);
        if (attempt === 0) yield delay(500);
      }
    }
  }

  yield put(Common.clearNotice());
  return evaluated;
};


/**
 * @function handleExtractReferences
 * @description Extracts and caches the reference coastlines for the selected imagery, the half of
 * the pipeline that needs no baseline and no transects. Runs once when the processing page opens.
 * The `key` fingerprints the inputs: a run whose key still matches the cached one is skipped
 * instead of redone, so reconfiguring transects never re-hits the imagery. Draws the raw
 * coastlines as it goes and leaves `extraction.status === 'READY'`, the gate `handleRun` waits on.
 * @param {Object} action - Redux action, with the `extractReferences` action creator's payload.
 * @see {@link extractCandidatesInChunks} - Does the per-image work.
 * @see {@link handleRun} - The transect-driven half, which consumes this cache.
 * **Called by:
 * @see {EXTRACT_REFERENCES}
 * @memberof module:ShoreS
 * @returns {void}
 */
const handleExtractReferences = function* ({ payload: { selectedImages, key } }) {
  const cached = yield select((s) => s.shoreSAnalysis.extraction);
  if (cached.status === 'RUNNING') return;
  if (cached.status === 'READY' && cached.key === key) return;

  yield put(
    Actions.setExtraction({
      status: 'RUNNING',
      error: null,
      key,
      requested: selectedImages.length,
      extracted: 0,
    }),
  );

  try {
    if (!selectedImages.length) throw new Error('No images selected — pick imagery before processing.');

    const { satellite } = yield select(getAcquisitionParameters);
    const { modelBBox } = yield select((s) => s.shoreSModel);

    if (!modelBBox) {
      throw new Error('Modeled coastlines not available — upload and parse a model output file first.');
    }

    const roi = ee.Geometry.Rectangle(modelBBox);

    resetCoRegistration();

    const evaluated = yield call(extractCandidatesInChunks, selectedImages, satellite, roi);
    const { features, discarded } = keepUsableCandidates(evaluated);

    if (discarded) {
      console.warn(`[ShoreS] discarded ${discarded} candidate(s) with unusable geometry`);
    }
    if (!features.length) {
      throw new Error('No reference coastlines could be extracted from the selected images.');
    }

    const candidatesGeoJSON = { type: 'FeatureCollection', features };

    yield put(Map.removeShapeGroup(RAW_COASTLINE_GROUP));
    yield call(
      drawFeatures,
      toDisplayFeatures(features, { smooth: true }),
      'forms.shoreS.map.legendRaw',
      RAW_COLOR,
      RAW_COASTLINE_GROUP,
    );

    yield put(
      Actions.setExtraction({ status: 'READY', candidatesGeoJSON, extracted: features.length, error: null }),
    );
  } catch (e) {
    console.error('[ShoreSAnalysis] reference extraction failed', e);
    yield put(Actions.setExtraction({ status: 'ERROR', error: e.message || String(e) }));
  }
};


/**
 * @function cleanCandidatesInChunks
 * @description Runs step 2 of the extraction over the cached candidates, in chunks: picks the
 * segment crossing the most transects and smooths it. A failed chunk is logged and skipped rather
 * than aborting the run.
 * @param {Array<Object>} candidateFeatures - Raw candidate features from the cached extraction.
 * @param {Object} transects - ee.FeatureCollection of the generated transects.
 * @see {@link cleanReferenceCandidate} - Runs per candidate.
 * **Called by:
 * @see {handleRun}
 * @memberof module:ShoreS
 * @returns {Array<Object>} The cleaned GeoJSON features.
 */
const cleanCandidatesInChunks = function* (candidateFeatures, transects) {
  const total = candidateFeatures.length;
  const timer = new Timer();
  const cleaned = [];

  /** The title names the job once; the detail is the part that moves. */
  const progress = (done, etc = '') =>
    `${done}/${total}${etc ? ` [${i18n.t('forms.shorelineAnalysis.process.estimate')} ${etc}]` : ''}`;
  const title = i18n.t('forms.shoreS.analysis.cleaning');

  yield put(Common.notice(title, progress(0)));

  const chunks = chunk(candidateFeatures, CLEANUP_CHUNK_SIZE);

  for (let index = 0; index < chunks.length; index++) {
    try {
      yield delay(10);
      const job = ee.List(chunks[index].map((feature) => cleanReferenceCandidate(ee.Feature(feature), transects)));
      const data = yield callback([job, job.evaluate]);
      cleaned.push(...(data || []).filter(Boolean));

      yield put(Common.notice(title, progress(cleaned.length, timer.format(timer.project(index + 1, chunks.length)))));
    } catch (e) {
      console.error(`[ShoreS] cleanup chunk ${index + 1}/${chunks.length} failed:`, e);
    }
  }

  yield put(Common.clearNotice());
  return cleaned;
};

/**
 * @function handleRun
 * @description Runs the transect-driven half of the Shore-S validation. The reference-coastline
 * extraction is not part of this: it runs earlier, in `handleExtractReferences`, and this bails
 * out unless it left `extraction.status === 'READY'`. The steps are:
 * 1. Reprojects the modeled coastlines and drawn baseline into the model's native (projected) CRS.
 * 2. (Re)generates orthogonal transects along the baseline server-side, from spacing/extent only
 *    (see `Coastline.generateOrthogonalTransects`) — never trusting client-drawn transect geometry.
 * 3. Cleans the cached reference candidates with those transects (see `cleanCandidatesInChunks`).
 * 4. Intersects transects with both modeled and reference coastlines, computes baseline-relative
 *    distances, and pairs them by time proximity (see `algorithms/shoreS`'s geospatial/merge/metrics
 *    functions).
 * 5. Renders the modeled/reference coastline and transect (colored by bias sign) map layers, then
 *    publishes the full result via `Actions.setResult`.
 * @param {Object} action - Redux action, with the `run` action creator's payload.
 * **Called by:
 * @see {RUN}
 * @memberof module:ShoreS
 * @returns {void}
 */
const handleRun = function* ({ payload: { baselineCoords, transectsStep, transectsExtent, mergeOptions } }) {
  yield put(Actions.setLoading(true));
  yield put(Actions.setError(null));

  try {
    const { crs, modeledCoastlinesGeoJSON } = yield select((s) => s.shoreSModel);
    const extraction = yield select((s) => s.shoreSAnalysis.extraction);

    if (!crs || !modeledCoastlinesGeoJSON) {
      throw new Error('Modeled coastlines not available — upload and parse a model output file first.');
    }
    if (extraction.status !== 'READY' || !extraction.candidatesGeoJSON?.features?.length) {
      throw new Error('Reference coastlines have not been extracted yet.');
    }

    const workingCrs = crs;

    const modeledFC = reprojectFeatureCollection(modeledCoastlinesGeoJSON, crs, workingCrs);

    const baselineFeature = baselineCoordsToFeature(reprojectLineString(baselineCoords, WGS84, workingCrs));

    // Same approach as Shore-R/standard shoreline `performAnalysis`: only the
    // spacing/extent numbers travel to the backend, which (re)generates the
    // orthogonal transects itself (GEE) rather than trusting whatever preview
    // geometry was drawn client-side.
    const transects = yield call(Coastline.generateOrthogonalTransects, baselineCoords, transectsStep, transectsExtent);

    const transectsRaw = yield call(evaluateFeatureCollection, ee.FeatureCollection(transects));
    const transectsWGS84 = {
      ...transectsRaw,
      features: transectsRaw.features.map((f, i) => ({
        ...f,
        properties: { ...f.properties, transect_id: i + 1 },
      })),
    };
    const transectsFC = reprojectFeatureCollection(transectsWGS84, WGS84, workingCrs);

    const cleaned = yield call(cleanCandidatesInChunks, extraction.candidatesGeoJSON.features, transects);

    if (!cleaned.length) {
      throw new Error('No reference coastlines survived the transect cleanup.');
    }

    const referenceCoastlinesWGS84 = normalizeReferenceTimeProperty({
      type: 'FeatureCollection',
      features: cleaned,
    });
    const referenceCoastlinesGeoJSON = reprojectFeatureCollection(referenceCoastlinesWGS84, WGS84, workingCrs);

    yield put(Map.removeShapeGroup(Metadata.FeatureType.TRANSECT));
    yield put(Map.removeShapeGroup(RAW_COASTLINE_GROUP));
    yield put(Map.removeShapeGroup(REFERENCE_COASTLINE_GROUP));

    yield call(
      drawFeatures,
      toDisplayFeatures(referenceCoastlinesWGS84.features),
      'forms.shoreS.map.legendReference',
      REFERENCE_COLOR,
      REFERENCE_COASTLINE_GROUP,
    );

    const baselinePointsByTransect = intersectTransectsWithBaseline(transectsFC, baselineFeature);

    const modeledIntersections = keepOnlyIntersectionClosestToBaseline(
      intersectTransectsToCoastlines(transectsFC, modeledFC),
      baselinePointsByTransect,
    );
    const referenceIntersections = keepOnlyIntersectionClosestToBaseline(
      intersectTransectsToCoastlines(transectsFC, referenceCoastlinesGeoJSON),
      baselinePointsByTransect,
    );

    const modeledDistances = withDateTime(computeDistancesFromBaseline(baselinePointsByTransect, modeledIntersections));
    const referenceDistances = withDateTime(
      computeDistancesFromBaseline(baselinePointsByTransect, referenceIntersections),
    );

    const mergedDistances = mergeByTimeProximity(modeledDistances, referenceDistances, mergeOptions);

    // Draw the transects exactly ONCE, colored by the sign of each transect's
    // bias (blue: modeled distance beyond the observed one; red: short of it;
    // purple: no paired data). The `finally` keeps them visible even when the
    // metrics throw.
    let colorOfTransect = () => TRANSECT_COLOR;
    let rowsWithError;
    let errorMetrics;
    let aggregatedMetrics;
    try {
      ({ rowsWithError, metrics: errorMetrics } = computeErrorMetrics(mergedDistances));
      aggregatedMetrics = computeAggregatedErrorMetrics(rowsWithError);

      const biasByTransect = {};
      aggregatedMetrics.byTransect.forEach((r) => {
        biasByTransect[r.transectId] = r.bias;
      });
      colorOfTransect = (feature) => {
        const bias = biasByTransect[feature.properties.transect_id];
        if (!Number.isFinite(bias)) return TRANSECT_COLOR;
        return bias >= 0 ? BIAS_POSITIVE_COLOR : BIAS_NEGATIVE_COLOR;
      };
    } finally {
      yield call(
        drawFeatures,
        transectsWGS84.features,
        'forms.map.transects.title',
        colorOfTransect,
        Metadata.FeatureType.TRANSECT,
      );
    }

    const measuredDeviation = computeMeasuredDeviation(modeledDistances, referenceDistances, rowsWithError);
    const bss = computeBrierSkillScore(errorMetrics.rmseFinal, measuredDeviation.rmsm);

    yield put(
      Actions.setResult({
        referenceCoastlinesGeoJSON,
        modeledDistances,
        referenceDistances,
        mergedDistances,
        errorMetrics: { ...errorMetrics, rmsm: measuredDeviation.rmsm, biasm: measuredDeviation.biasm, bss },
        aggregatedMetrics,
        measuredDeviation,
      }),
    );
  } catch (e) {
    console.error('[ShoreSAnalysis] run failed', e);
    yield put(Actions.setError(e.message || String(e)));
  } finally {
    yield put(Actions.setLoading(false));
  }
};

/**
 * @function handleRedrawRaw
 * @description Redraws the raw reference coastlines from the cached extraction. Runs on
 * RESET_ANALYSIS, which clears the analysis result and its map layers but deliberately keeps the
 * extraction — so the raw layer has to be put back for the next run.
 * **Called by:
 * @see {RESET_ANALYSIS}
 * @memberof module:ShoreS
 * @returns {void}
 */
const handleRedrawRaw = function* () {
  const { status, candidatesGeoJSON } = yield select((s) => s.shoreSAnalysis.extraction);
  if (status !== 'READY' || !candidatesGeoJSON?.features?.length) return;

  yield put(Map.removeShapeGroup(RAW_COASTLINE_GROUP));
  yield call(
    drawFeatures,
    toDisplayFeatures(candidatesGeoJSON.features, { smooth: true }),
    'forms.shoreS.map.legendRaw',
    RAW_COLOR,
    RAW_COASTLINE_GROUP,
  );
};

/**
 * @function root
 * @description Root saga for the Shore-S analysis duck, wiring each action type to its handler
 * through `bufferedHandler` so a second dispatch can't overlap a run in progress.
 * **Called by:
 * @see {Redux store} - registered as the "shoreSAnalysis" saga.
 * @memberof module:ShoreS
 * @returns {void}
 */
const root = function* () {
  yield all([
    bufferedHandler(Types.EXTRACT_REFERENCES, handleExtractReferences),
    bufferedHandler(Types.RUN, handleRun),
    bufferedHandler(Types.RESET_ANALYSIS, handleRedrawRaw),
  ]);
};

export { REFERENCE_COASTLINE_GROUP, RAW_COASTLINE_GROUP };

export default root;
